import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { WORKER_TOKEN_KEY } from '../constants/tokenKeys';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const QR_BASE_URL = process.env.REACT_APP_QR_BASE_URL;

const WorkerContext = createContext(undefined);

export const WorkerProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [qrStatus, setQrStatus] = useState("");
  const [qrLink, setQrLink] = useState("");

  const [messageCount, setMessageCount] = useState(0);
  const updateMessageCount = (count) => setMessageCount(count);


  // Get auth headers with token
  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  }), [token]);

  // Get multipart auth headers for file uploads
  const getMultipartAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    "Content-Type": "multipart/form-data",
  }), [token]);

  // Persist token to localStorage
  const persistToken = (newToken) => {
    localStorage.setItem(WORKER_TOKEN_KEY, newToken);
    setToken(newToken);
  };

  // Clear session
  const clearSession = useCallback(() => {
    localStorage.removeItem(WORKER_TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  // Verify token with backend
  const verifyToken = useCallback(async (tokenToVerify) => {
    try {
      const response = await axios.get(`${BASE_URL}/lenskart/verify-token`, {
        headers: { Authorization: `Bearer ${tokenToVerify}` },
      });

      if (response.data?.error?.name === "TokenExpiredError") {
        clearSession();
        return false;
      }

      if (response.data?.user) {
        const u = response.data.user;
        setUser({
          id: u.id,
          phone: u.phone,
          name: u.name,
          workerCode:u.workerCode,
          gender: u.gender,
          skills: u.skills || [],
          skillIds: u.skillIds || [],
          panCard: u.panNumber,
          preferredLocation: u.preferredLocation,
          preferredLocationId: u.preferredLocationId,
          selectedSlot: u.shifttime,
          selectedWeekendSlot: u.weekend_shift,
          profilePhotoUrl: u.profilePhotoUrl,
          dob: u.dob,
          address: u.address,
          isBlacklisted: u.verification?.blacklist,
          isAgreementSigned: u.isAgreementSigned,
          aadharVerified: u.isAadhaarVerified,
          accountverified:u?.accountVerification?.status,
          accountverifiedremarks:u?.accountVerification?.rejectionReason,
          refcheckverified:u?.familyDetails?.isReferenceChecked,
          refcheckremarks:u?.familyDetails?.Remarks,
          department: u.department,
          currentStatus: u.status,
          relativeNumber: u.familyDetails?.RelativePhone,
          lastcompanyName: u.lastCompany,
          highestQualification: u.highestQualification,
          relativeName: u.familyDetails?.RelativeName,
          payoutFrequency:u.payoutFrequency,
          relativeType: u.familyDetails?.RelativeType,
          temporaryAddress: u.temporaryAddress,
          isAuthenticated: true,
          isAdmin: u.role === "Supervisor" || 
                   (u.role === "Admin" && u.featureAccess?.includes("Mark Attendance")),
        });
      }

      return true;
    } catch (error) {
      console.error("Token verification failed:", error);
      clearSession();
      return false;
    }
  }, [clearSession]);

  // Restore session on mount
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const storedToken = localStorage.getItem(WORKER_TOKEN_KEY);
        if (storedToken) {
          setToken(storedToken);
          await verifyToken(storedToken);
        }
      } catch (error) {
        console.error("Session restore failed:", error);
        clearSession();
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };
    restoreSession();
  }, []);

  // auth functions

  const sendOtp = async (phone, isWhatsapp = false) => {
  try {
    setLoading(true);

    // Always grab the token — prefer window (injected by RN), fall back to localStorage
    let appToken = null;
    try {
      appToken =
        window?.fcmToken ||
        window?.__fcmToken ||
        localStorage.getItem('fcmToken') ||
        null;
    } catch (e) {
      
    }

    console.log('📱 Worker sendOtp appToken:', appToken ? `${appToken.substring(0, 15)}... (len ${appToken.length})` : 'NULL');

    const response = await axios.post(
      `${BASE_URL}/lenskart/initiate`,
      {
        phone: "91" + phone,
        isWhatsapp,
        appToken,            
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return response.data.userId;
  } catch (error) {
    console.error("Send OTP failed:", error);
    throw error;
  } finally {
    setLoading(false);
  }
};

  const verifyOtp = async (userId, otp, extra = {}) => {
  try {
    setLoading(true);
    const response = await axios.post(
      `${BASE_URL}/lenskart/verify-login-otp`,
      { userId, otp, ...extra },
      { headers: { "Content-Type": "application/json" } }
    );

    if (response.data.token) {
      persistToken(response.data.token);
      await verifyToken(response.data.token);
    }

    return response.data;
  } catch (error) {
    console.error("Verify OTP failed:", error);
    throw error;
  } finally {
    setLoading(false);
  }
};

  const logout = () => {
    clearSession();
  };

  //Bank Details

  const fetchBankDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/lenskart/account-details`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch bank details:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateBankDetails = async (bankDetails, hasExisting = false) => {
  const method = hasExisting ? "put" : "post";
  try {
    const response = await axios[method](
      `${BASE_URL}/lenskart/account-details`,
      bankDetails,
      { headers: getAuthHeaders() }
    );

    // 🛡️ Backend returned HTTP 200 but with success: false
    if (response.data && response.data.success === false) {
      const serverMessage =
        response.data.message ||
        response.data.error ||
        response.data.details ||
        "Failed to update bank details";
      const err = new Error(serverMessage);
      err.response = response;
      err.isApiFailure = true;
      throw err;
    }

    return response.data;
  } catch (error) {
    // If it's already our normalized error, just re-throw it
    if (error.isApiFailure) throw error;

    const serverMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.response?.data?.details ||
      error?.message ||
      "Failed to update bank details";

    const normalized = new Error(serverMessage);
    normalized.response = error.response;
    normalized.original = error;
    throw normalized;
  }
};

  //User Stats

  const fetchUserStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/lenskart/userStats`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch user stats:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  //Companies & Departments

  const fetchCompanies = async (preferredLocationId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/onDemandCompanies/list-companies/${preferredLocationId}`,
        { headers: getAuthHeaders() }
      );
      return response.data.companies || [];
    } catch (error) {
      console.error("Failed to fetch companies:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async (companyId, skills, skillIds) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/onDemandCompanies/get-department`,
        { companyId, skills, skillIds },
        { headers: getAuthHeaders() }
      );
      return response.data.departments || [];
    } catch (error) {
      console.error("Failed to fetch departments:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  //QR Code

  const generateAttendanceQR = (userId, currentDay, status) => {
    return `${QR_BASE_URL}/lkart/admin/attendance?userId=${userId}&currentDay=${currentDay}&status=${status}`;
  };

  const fetchQRLink = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/lenskart/qr`,
        {},
        { headers: getAuthHeaders() }
      );

      const qrLinkGenerated = generateAttendanceQR(
        response.data.data.userId,
        response.data.data.currentDay,
        response.data.data.status
      );

      setQrLink(qrLinkGenerated);
      setQrStatus(response.data.data.status);

      return {
        link: qrLinkGenerated,
        status: response.data.data.status
      };
    } catch (error) {
      console.error('QR link fetch failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  //Agreement

  const fetchDownloadLink = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/lenskart/download-link`,
        { headers: getAuthHeaders() }
      );
      return response.data.link;
    } catch (error) {
      console.error('Download link fetch failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const acceptAgreement = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/lenskart/accept-agreement`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to accept agreement:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  //Personal Details + Profile

  const updatePersonalDetails = async (updatedData) => {
    try {
      setLoading(true);
      const response = await axios.put(
        `${BASE_URL}/lenskart/update-profile`,
        updatedData,
        { headers: getAuthHeaders() }
      );
      return response.data.user;
    } catch (error) {
      console.error("Failed to update personal details:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const uploadSelfie = async (imageFile) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('profilePhoto', imageFile);

      const response = await axios.post(
        `${BASE_URL}/lenskart/upload-profile-photo`,
        formData,
        { headers: getMultipartAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to upload selfie:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateDobAndAddress = async (data) => {
    try {
      setLoading(true);
      const response = await axios.put(
        `${BASE_URL}/lenskart/update-dob-address`,
        data,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update DOB and address:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  //Aadhaar Verification

  const initiateAadhaarVerification = async (aadhaarNo) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/lenskart/initiate-aadhaar-verification`,
        { aadhaarNo },
        { headers: getAuthHeaders() }
      );
      return response.data.requestId;
    } catch (error) {
      console.error('Aadhaar verification initiation failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const verifyAadhaar = async (requestId, otp, aadhaarNo) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/lenskart/verify-aadhaar-otp`,
        { requestId, otp, aadhaarNo },
        { headers: getAuthHeaders() }
      );
      return response.data.isAadhaarVerified;
    } catch (error) {
      console.error('Aadhaar verification failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  //Messages

  const getMessageByPhone = async (phone) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/getMessageByPhone`,
        { phone },
        { headers: getAuthHeaders() }
      );
      if (response.data.success) {
        setMessageCount(response.data.count || 0);
      }
      return response.data;
    } catch (error) {
      console.error("Failed to fetch message requests:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const submitMessageResponse = async (messageId, response) => {
    try {
      setLoading(true);
      const responseData = await axios.post(
        `${BASE_URL}/lenskart-admin/submit-response`,
        { messageId, response },
        { headers: getAuthHeaders() }
      );
      return responseData.data;
    } catch (error) {
      console.error("Failed to submit response:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const markMessageAsRead = async (messageId) => {
    try {
      setLoading(true);
      const result = await axios.post(
        `${BASE_URL}/lenskart-admin/read-notification`,
        { messageId },
        { headers: getAuthHeaders() }
      );
      return result.data;
    } catch (error) {
      console.error("Failed to mark message as read:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  //Payments

  const fetchPaymentSummary = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/lenskart/payment-summary`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch payment summary:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceHistory = async (params = {}) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/lenskart/attendance-history`,
        { headers: getAuthHeaders(), params }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch attendance history:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const applyForJob = async (jobData) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/lenskart/apply`,
        jobData,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Job application failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  //Job Details

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/lenskart/factory`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Fetching job details failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  //Referrals

  const fetchReferredWorkers = async (userId) => {
  try {
    setLoading(true);
    const response = await axios.get(
      `${BASE_URL}/lenskart/list-referred-workers/${userId}`,
      { headers: getAuthHeaders() }
    );
    return response.data.workers || response.data || [];
  } catch (error) {
    console.error("Failed to fetch referred workers:", error);
    throw error;
  } finally {
    setLoading(false);
  }
};

  const fetchMonthlyPayout = async (workerId)=>{
    try{
        setLoading(true);
        const response = await axios.get(
            `${BASE_URL}/lenskart/worker-monthly-referral-payout/${workerId}`,
            { headers: getAuthHeaders()}
        );
        return response.data;
    } catch(error){
        console.error("Failed to fetch monthly payout:", error);
        throw error;
    } finally {
        setLoading(false);
    }
  };


    const changepayoutmode = async (mode, appliedFromISO) => {
  try {

    setLoading(true);
    
const workerId=user.id;
    const response = await axios.post(
      `${BASE_URL}/lenskart-admin/worker-payout-frequency`,
      {
        workerId,
        payoutFrequency: mode.toLowerCase(),
        appliedFrom: appliedFromISO,
      },
      {
         headers: {
          Authorization: `Bearer ${token}`,
          
        }
      }
    );

    return response;
  } catch (error) {
    console.error("Failed to change payout mode:", error);
    throw error;
  } finally {
    setLoading(false);
  }
};



const worker_payout_frequency_history = async (workerId) => {
  try {
    setLoading(true);
    const response = await axios.post(
      `${BASE_URL}/lenskart-admin/worker-frequency-history`,
      { workerId },
      { headers: getAuthHeaders()}
    );

    return response.data;
  } catch (error) {
    console.error("Failed to fetch payout history:", error);
    return { success: false, data: { history: [] } };
  } finally {
    setLoading(false);
  }
};



  

  const fetchMonthlyInsentive = async (workerId)=>{
    try{
        setLoading(true);
        const response = await axios.get(
            `${BASE_URL}/lenskart/worker-monthly-incentive-payout/${workerId}`,
            { headers: getAuthHeaders()}
        );
        return response.data;
    } catch(error){
        console.error('Failed to fetch monthly incentives:', error);
        throw error;
    } finally {
        setLoading(false);
    }
  }

    const fetchKYWquestions = async ()=>{
    try{
        setLoading(true);
        const response = await axios.get(
            `${BASE_URL}/kyw/questions`,
            { headers: getAuthHeaders()}
        );
        return response.data;
    } catch(error){
        console.error('Failed to fetch Questions:', error);
        throw error;
    } finally {
        setLoading(false);
    }
  }

  const fetchProofOfWorkLink = async () => {
  try {
    const token = localStorage.getItem(WORKER_TOKEN_KEY);
    const response = await axios.get(
      `${BASE_URL}/lenskart/proof-of-work-link`, // your backend endpoint that returns { url }
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data.url; // return the URL string
  } catch (error) {
    console.error("Failed to fetch proof of work link:", error);
    throw error;
  }
};

const fetchAgreementUrl = async (name) => {
  try {
    setLoading(true);
    const response = await axios.post(
      `${BASE_URL}/agreement/generate`,
      { name },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const publicUrl = response.data?.data?.publicUrl;
    if (!publicUrl) {
      console.error("Agreement generate response:", response.data);
      throw new Error(
        response.data?.message || response.data?.error || "Agreement URL missing in response"
      );
    }
    return publicUrl;
  } catch (error) {
    console.error("Fetching agreement URL failed:", error);
    throw error;
  } finally {
    setLoading(false);
  }
};

  

  const value = {
    // State
    token,
    user,
    setUser,
    loading,
    initialized,
    qrStatus,
    qrLink,
    isLoggedIn: !!token,
    
    // Auth functions
    sendOtp,
    verifyOtp,
    logout,
    verifyToken,
    
    // Bank details
    fetchBankDetails,
    updateBankDetails,
    
    // User stats
    fetchUserStats,
    
    // Companies & departments
    fetchCompanies,
    fetchDepartments,
    fetchAgreementUrl,
    
    // QR code
    fetchQRLink,
    
    // Agreement
    fetchDownloadLink,
    acceptAgreement,
    fetchProofOfWorkLink,
    
    // Profile & personal details
    updatePersonalDetails,
    uploadSelfie,
    updateDobAndAddress,
    
    // Aadhaar verification
    initiateAadhaarVerification,
    verifyAadhaar,
    
    // Messages
    getMessageByPhone,
    submitMessageResponse,
    markMessageAsRead,
    messageCount,
    updateMessageCount,
    
    // Payments
    fetchPaymentSummary,
    fetchAttendanceHistory,
    applyForJob,
    
    // Job details
    fetchJobDetails,

    // Referrals
    fetchReferredWorkers,
    fetchMonthlyPayout,
    fetchMonthlyInsentive,

    //change payout mode
    worker_payout_frequency_history,
    changepayoutmode,


    //KYW questions list
    fetchKYWquestions,

  };

  // if (!initialized) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center bg-gray-50">
  //       <p className="text-gray-500 text-sm">Loading...</p>
  //     </div>
  //   );
  // }

  return (
    <WorkerContext.Provider value={value}>
      {children}
    </WorkerContext.Provider>
  );
};

export const useWorker = () => {
  const ctx = useContext(WorkerContext);
  if (!ctx) {
    throw new Error("useWorker must be used inside <WorkerProvider>");
  }
  return ctx;
};